import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Field } from '@vnetwork/ui/forms';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Input,
  Card, CardHeader, CardTitle, CardBody,
} from '@vnetwork/ui/primitives';
import { useTicketCustomerServices, type CustomerService } from '@/api/tickets-workflow';
import { CustomerSearchSelect } from '@/pages/contracts/new/customer-search-select';
import type { TicketFormValues } from '../ticket-form-schema';

const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--vn-space-4)',
} as const;

/** Live API shape includes assignee arrays not yet in the TS type. */
type CustomerServiceFull = CustomerService & {
  cs_assignees?: number[];
  sale_assignees?: number[];
};

interface CustomerServiceSectionProps {
  /** When provided (edit mode), the customer picker is replaced by a read-only display. */
  lockedCustomerId?: number;
  lockedCustomerName?: string;
}

/**
 * Customer + Service picker section.
 *
 * Customer: uses CustomerSearchSelect (debounced async search, single control).
 * Service list: loaded from `GET /tickets/customers/:id/services` after customer
 * is selected; includes `service_interestings` (POC / pre-sales items).
 *
 * On service_interesting selection: sets `ticket_product_name` and clears
 * `service_id` (matching legacy handleServiceChange).
 */
export function CustomerServiceSection({
  lockedCustomerId,
  lockedCustomerName,
}: CustomerServiceSectionProps) {
  const { control, setValue, getValues, formState: { errors } } = useFormContext<TicketFormValues>();

  const customerId = useWatch({ control, name: 'customer_id' });
  const typeKey = useWatch({ control, name: 'ticket_type_key' });
  const isInternal = typeKey === 'internal_problem';

  const effectiveCustomerId = customerId != null && customerId > 0 ? customerId : 0;
  const { data: servicesRes, isLoading: servicesLoading } = useTicketCustomerServices(
    effectiveCustomerId,
  );
  const services = (servicesRes?.result?.services ?? []) as CustomerServiceFull[];
  const serviceInterestings = servicesRes?.result?.service_interestings ?? [];

  if (isInternal) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer &amp; Service</CardTitle>
      </CardHeader>
      <CardBody>
      <div style={twoColGrid}>

        {/* Customer picker — locked (read-only) in edit mode, searchable on create */}
        {lockedCustomerId ? (
          <Field label="Customer" required>
            {(fp) => (
              <Input
                {...fp}
                value={lockedCustomerName ?? String(lockedCustomerId)}
                readOnly
              />
            )}
          </Field>
        ) : (
          <CustomerSearchSelect
            label="Customer"
            required
            value={customerId ?? 0}
            onChange={(id) => {
              setValue('customer_id', id);
              setValue('service_id', 0);
              setValue('ticket_product_name', '');
            }}
            error={String(errors.customer_id?.message ?? '') || undefined}
          />
        )}

        {/* Service picker — shows after customer selected */}
        {effectiveCustomerId > 0 && (
          <Field
            label="Service"
            error={String(errors.ticket_product_name?.message ?? '') || undefined}
            style={{ gridColumn: 'span 2' }}
          >
            {(fp) => (
              <Controller
                name="service_id"
                control={control}
                render={({ field }) => {
                  /* Resolve the selected label ourselves: options load async, and a
                     disabled (locked) Radix Select never re-resolves SelectValue for
                     items that mounted after the value was set via reset(). */
                  const selectedService = services.find(
                    (s) => s.service_id === Number(field.value),
                  );
                  return (
                    <Select
                      value={
                        field.value && Number(field.value) > 0
                          ? String(field.value)
                          : ''
                      }
                      onValueChange={(v) => {
                        /* Radix reset-echo guard: Radix echoes onValueChange when the
                           controlled value is set programmatically (reset() on edit page).
                           Only a real user selection changes the value — compare with live
                           form value to ignore stale-closure echoes. */
                        if (!v) return;
                        if (!v.startsWith('interesting:') && Number(v) === getValues('service_id')) return;
                        if (v.startsWith('interesting:')) {
                          const label = v.replace('interesting:', '');
                          setValue('service_id', 0);
                          setValue('ticket_product_name', label);
                          const found = serviceInterestings.find((si) => si.label === label);
                          if (found?.cs_assignees?.length) {
                            setValue('ticket_cs_assignees', [...new Set(found.cs_assignees)]);
                          }
                          if (found?.sale_assignees?.length) {
                            setValue('ticket_sale_assignees', [...new Set(found.sale_assignees)]);
                          }
                          field.onChange(0);
                        } else {
                          const id = Number(v);
                          field.onChange(id);
                          setValue('ticket_product_name', '');
                          const svc = services.find((s) => s.service_id === id);
                          if (svc) {
                            if (svc.service_start_date) {
                              setValue('service_startdate', svc.service_start_date);
                            }
                            if (svc.service_stopped_date) {
                              setValue('service_duedate', svc.service_stopped_date);
                            }
                            if (svc.cs_assignees?.length) {
                              setValue('ticket_cs_assignees', [...new Set(svc.cs_assignees)]);
                            }
                            if (svc.sale_assignees?.length) {
                              setValue('ticket_sale_assignees', [...new Set(svc.sale_assignees)]);
                            }
                          }
                        }
                      }}
                    >
                      <SelectTrigger {...fp} invalid={!!errors.ticket_product_name}>
                        <SelectValue
                          placeholder={servicesLoading ? 'Loading services…' : 'Select service'}
                        >
                          {selectedService?.service_name}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s.service_id} value={String(s.service_id)}>
                            {s.service_name}
                            {s.is_poc ? ' (POC)' : ''}
                            {' '}
                            <span style={{ color: 'var(--vn-fg-muted)' }}>
                              — {s.product_group_name}
                            </span>
                          </SelectItem>
                        ))}
                        {serviceInterestings.length > 0 && (
                          <>
                            {serviceInterestings.map((si) => (
                              <SelectItem key={`si-${si.value}`} value={`interesting:${si.label}`}>
                                {si.label}
                                <span style={{ color: 'var(--vn-fg-muted)' }}> — Interested</span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  );
                }}
              />
            )}
          </Field>
        )}

        {/* Fallback product name when no service matches */}
        <Field
          label="Product / Service Name (if not listed)"
          error={String(errors.ticket_product_name?.message ?? '') || undefined}
          style={{ gridColumn: 'span 2' }}
        >
          {(fp) => (
            <Controller
              name="ticket_product_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...fp}
                  {...field}
                  placeholder="Enter product or service name"
                  invalid={!!errors.ticket_product_name}
                />
              )}
            />
          )}
        </Field>
      </div>
      </CardBody>
    </Card>
  );
}
